/**
 * MONSTER TOWN — ANIMAL PICTURES. ZOO CREW (src/venues/zoo/**).
 *
 * The 2D half of `art.ts`: the same six creatures, drawn flat for the feeding
 * cards and the collection book. Same hand as `src/ui/icons.ts` and
 * `src/cooking/pics.ts` — one viewBox, one ink colour, stroke 7 for silhouette
 * and 5 for detail, fills from the palette, no raw hex, no external assets.
 *
 * A picture is derived from the animal's DATA ROW, so a new species drawn into
 * `ANIMALS` gets a card without anybody drawing a card.
 */

import { css } from '../../core/palette'
import { svgEl } from '../../cooking/pics'
import type { AnimalDef, HabitatId } from '../../content/animals'
import type { AnimalShape } from './art'

const INK = css('ink')
const S = `stroke="${INK}" stroke-linecap="round" stroke-linejoin="round"`
const OUT = `${S} stroke-width="7"`
const DET = `${S} stroke-width="5"`

/** Eyes, blush and a smile. Every animal has the same face; only ears differ. */
function faceBits(cx: number, cy: number, r: number): string {
  const ex = r * 0.4
  return `
    <circle cx="${cx - ex}" cy="${cy - r * 0.1}" r="${r * 0.14}" fill="${INK}"/>
    <circle cx="${cx + ex}" cy="${cy - r * 0.1}" r="${r * 0.14}" fill="${INK}"/>
    <circle cx="${cx - r * 0.72}" cy="${cy + r * 0.24}" r="${r * 0.13}" fill="${css('blush')}"/>
    <circle cx="${cx + r * 0.72}" cy="${cy + r * 0.24}" r="${r * 0.13}" fill="${css('blush')}"/>
    <path d="M${cx - r * 0.26} ${cy + r * 0.34} Q${cx} ${cy + r * 0.62} ${cx + r * 0.26} ${cy + r * 0.34}"
      ${DET} fill="none"/>`
}

/**
 * ===========================================================================
 *  SIX SILHOUETTES, NOT SIX HUES (fixed vr1 — MAJOR)
 * ===========================================================================
 * Critic, on the feeding sheet: *"the zoo's animal cards are three variations
 * of one bear … Four species: rabbit, penguin, bear, bear. Three of the four
 * share the same round body silhouette and two differ only in hue and ear
 * colour. As collection art they are not distinguishable at a glance, which is
 * the opposite of what a zoo teaches."* Correct: every animal was drawn from
 * one body plan at one size (`rx 28 / ry 22`, head `r 22`) and the only shape
 * that ever moved was the giraffe's neck. `big` and `tiny` were two circles at
 * cx 26/74 r 12 and cx 30/70 r 11 — the same animal in two colours.
 *
 * A three-year-old sorts by OUTLINE first, so the outline is now the data:
 * every shape gets its own proportions (a mouse is small and low, a bear is
 * broad and heavy, a penguin is upright and narrow) on top of its own ears.
 * Squint at the four starter cards and they are four different animals.
 */
interface BodyPlan {
  /** Belly ellipse. */
  readonly bx: number
  readonly by: number
  readonly bcy: number
  /** Head. */
  readonly hr: number
  readonly hcy: number
  /** A giraffe's neck. */
  readonly neck?: boolean
}

const PLAN: Record<AnimalShape, BodyPlan> = {
  // long ears, ordinary build
  hop: { bx: 27, by: 22, bcy: 72, hr: 21, hcy: 40 },
  // upright and NARROW: a standing bird, not a ball
  waddle: { bx: 22, by: 26, bcy: 68, hr: 18, hcy: 32 },
  // BROAD and heavy, head low and wide — reads as "big" with no colour at all
  big: { bx: 34, by: 25, bcy: 70, hr: 25, hcy: 35 },
  // SMALL and low to the ground, with a tail. The whole animal is 60 % of a bear
  tiny: { bx: 18, by: 14, bcy: 82, hr: 15, hcy: 60 },
  // slim, long-armed, side ears and a curly tail
  climb: { bx: 23, by: 21, bcy: 74, hr: 20, hcy: 42 },
  // the neck does the work
  tall: { bx: 21, by: 16, bcy: 81, hr: 21, hcy: 33, neck: true },
}

function ears(shape: AnimalShape, trim: string, body: string, p: BodyPlan): string {
  const top = p.hcy - p.hr
  switch (shape) {
    case 'hop':
      return `
        <ellipse cx="38" cy="${top - 6}" rx="7" ry="18" fill="${body}" ${OUT}/>
        <ellipse cx="62" cy="${top - 6}" rx="7" ry="18" fill="${body}" ${OUT}/>
        <ellipse cx="38" cy="${top - 6}" rx="3" ry="10" fill="${css('blush')}"/>
        <ellipse cx="62" cy="${top - 6}" rx="3" ry="10" fill="${css('blush')}"/>`
    case 'tall':
      return `
        <path d="M38 ${top + 4} L34 ${top - 10}" ${OUT} fill="none"/>
        <path d="M62 ${top + 4} L66 ${top - 10}" ${OUT} fill="none"/>
        <circle cx="34" cy="${top - 11}" r="5" fill="${trim}" ${DET}/>
        <circle cx="66" cy="${top - 11}" r="5" fill="${trim}" ${DET}/>`
    case 'waddle':
      // WINGS, not ears — and the bill is in FRONT of the face, so it is drawn
      // later (see `frontBits`). A bird with no wings is a blue bear.
      return `
        <ellipse cx="${50 - p.bx - 2}" cy="${p.bcy}" rx="8" ry="19" fill="${trim}" ${OUT}/>
        <ellipse cx="${50 + p.bx + 2}" cy="${p.bcy}" rx="8" ry="19" fill="${trim}" ${OUT}/>`
    case 'climb':
      return `
        <circle cx="${50 - p.hr - 6}" cy="${p.hcy - 6}" r="13" fill="${trim}" ${OUT}/>
        <circle cx="${50 + p.hr + 6}" cy="${p.hcy - 6}" r="13" fill="${trim}" ${OUT}/>`
    case 'tiny':
      // BIG round ears on a SMALL head — a mouse's proportions are the whole tell
      return `
        <circle cx="${50 - p.hr - 3}" cy="${top - 1}" r="11" fill="${css('blush')}" ${OUT}/>
        <circle cx="${50 + p.hr + 3}" cy="${top - 1}" r="11" fill="${css('blush')}" ${OUT}/>`
    case 'big':
      // small ears, tucked right on top of a wide head
      return `
        <circle cx="${50 - p.hr + 4}" cy="${top + 2}" r="9" fill="${trim}" ${OUT}/>
        <circle cx="${50 + p.hr - 4}" cy="${top + 2}" r="9" fill="${trim}" ${OUT}/>`
  }
}

/** Tails, feet and snouts — the rest of what makes an outline its own. */
function extras(shape: AnimalShape, trim: string, body: string, p: BodyPlan): string {
  switch (shape) {
    case 'climb':
      return `<path d="M78 76 C94 74 92 56 80 58" ${OUT} fill="none"/>`
    case 'tiny':
      // a long bare tail, curled — nothing else in the zoo has one
      return `<path d="M${50 + p.bx} ${p.bcy + 2} C82 88 92 76 86 66" ${DET} fill="none" stroke="${css('blush')}"/>
              <path d="M${50 + p.bx} ${p.bcy + 2} C82 88 92 76 86 66" stroke="${INK}" stroke-width="2.5" fill="none" stroke-linecap="round"/>`
    case 'waddle':
      // webbed feet, wide apart
      return `<ellipse cx="40" cy="93" rx="10" ry="5" fill="${trim}" ${DET}/>
              <ellipse cx="60" cy="93" rx="10" ry="5" fill="${trim}" ${DET}/>`
    case 'big':
      // four heavy paws under a heavy body
      return `<ellipse cx="30" cy="92" rx="10" ry="6" fill="${body}" ${OUT}/>
              <ellipse cx="70" cy="92" rx="10" ry="6" fill="${body}" ${OUT}/>`
    default:
      return ''
  }
}

/** Raw SVG body for one animal. */
export function animalSvgBody(a: AnimalDef): string {
  const body = css(a.body)
  const belly = css(a.belly)
  const trim = css(a.trim)
  const p = PLAN[a.shape]
  const noseR = Math.max(2.6, p.hr * 0.16)
  return `
    ${extras(a.shape, trim, body, p)}
    <ellipse cx="50" cy="${p.bcy}" rx="${p.bx}" ry="${p.by}" fill="${body}" ${OUT}/>
    <ellipse cx="50" cy="${p.bcy + 6}" rx="${Math.round(p.bx * 0.6)}" ry="${Math.round(p.by * 0.55)}" fill="${belly}" ${DET}/>
    ${p.neck === true ? `<path d="M50 ${p.bcy - p.by + 4} L50 ${p.hcy + p.hr - 4}" ${S} stroke-width="14" fill="none"/><path d="M50 ${p.bcy - p.by + 4} L50 ${p.hcy + p.hr - 4}" stroke="${body}" stroke-width="8" stroke-linecap="round" fill="none"/>` : ''}
    ${ears(a.shape, trim, body, p)}
    <circle cx="50" cy="${p.hcy}" r="${p.hr}" fill="${body}" ${OUT}/>
    <ellipse cx="50" cy="${p.hcy + p.hr * 0.36}" rx="${Math.round(p.hr * 0.5)}" ry="${Math.round(p.hr * 0.36)}" fill="${belly}" ${DET}/>
    <circle cx="50" cy="${p.hcy + p.hr * 0.45}" r="${noseR}" fill="${INK}"/>
    ${faceBits(50, p.hcy, p.hr)}
    ${frontBits(a, p)}`
}

/**
 * Anything that belongs IN FRONT of the face. The duck's bill was drawn with the
 * ears, which are drawn before the head — so the head covered it completely and
 * every "duck" was a bear. Layer order is the whole difference between six
 * species and four.
 */
function frontBits(a: AnimalDef, p: BodyPlan): string {
  if (a.shape !== 'waddle') return ''
  const trim = css(a.trim)
  const y = p.hcy + 4
  return `
    <path d="M34 ${y} L66 ${y} L60 ${y + 15} L40 ${y + 15} Z" fill="${trim}" ${OUT}/>
    <path d="M40 ${y + 7} L60 ${y + 7}" stroke="${INK}" stroke-width="4" stroke-linecap="round"/>`
}

/** An animal picture as a DOM element, sized in CSS px. */
export function animalEl(a: AnimalDef, sizePx: number): HTMLSpanElement {
  return svgEl(animalSvgBody(a), sizePx, 'mt-ico mt-zoo-pic')
}

// ---------------------------------------------------------------------------
// habitats
// ---------------------------------------------------------------------------

const HABITAT_PIC: Record<HabitatId, string> = {
  grass: `
    <rect x="10" y="58" width="80" height="32" rx="10" fill="${css('grass')}" ${OUT}/>
    <path d="M28 58 L24 40 M40 58 L38 34 M56 58 L60 36 M70 58 L74 42" ${OUT} fill="none"/>
    <circle cx="38" cy="30" r="7" fill="${css('flowerYellow')}" ${DET}/>
    <circle cx="72" cy="38" r="6" fill="${css('flowerWhite')}" ${DET}/>`,
  water: `
    <rect x="10" y="52" width="80" height="38" rx="16" fill="${css('water')}" ${OUT}/>
    <path d="M20 66 C28 60 34 72 42 66 C50 60 56 72 64 66 C72 60 76 70 82 66"
      stroke="${css('waterDeep')}" stroke-width="6" stroke-linecap="round" fill="none"/>
    <ellipse cx="62" cy="44" rx="16" ry="7" fill="${css('leafLight')}" ${DET}/>
    <circle cx="36" cy="40" r="8" fill="${css('flowerPink')}" ${DET}/>`,
  tree: `
    <path d="M50 90 L50 56" ${S} stroke-width="14" fill="none"/>
    <path d="M50 90 L50 56" stroke="${css('trunk')}" stroke-width="8" stroke-linecap="round" fill="none"/>
    <circle cx="50" cy="38" r="24" fill="${css('leaf')}" ${OUT}/>
    <circle cx="32" cy="48" r="14" fill="${css('leafLight')}" ${DET}/>
    <circle cx="68" cy="48" r="13" fill="${css('leafDark')}" ${DET}/>`,
  rock: `
    <path d="M12 88 L30 46 L54 88 Z" fill="${css('rockDark')}" ${OUT}/>
    <path d="M44 88 L64 56 L88 88 Z" fill="${css('rock')}" ${OUT}/>
    <circle cx="30" cy="60" r="4" fill="${css('flowerWhite')}" opacity="0.7"/>`,
}

export function habitatEl(h: HabitatId, sizePx: number): HTMLSpanElement {
  return svgEl(HABITAT_PIC[h], sizePx, 'mt-ico mt-zoo-hab')
}

// ---------------------------------------------------------------------------
// sounds
// ---------------------------------------------------------------------------

/**
 * WHAT IT SAYS, AS A PICTURE — ONE PER NOISE (fixed vr1 — MAJOR).
 *
 * There used to be one speaker drawing for the whole zoo, which meant the third
 * of the brief's three questions ("what does it say") looked identical on every
 * card. Critic: *"THE TWO DISCS ARE THE SAME ON ALL FOUR CARDS … nothing on the
 * feeding sheet differentiates any animal's habitat or sound from any other's."*
 *
 * A sound cannot be drawn, but what MAKES it can: a spring boings, a drop
 * plops, teeth munch, a nibble is a little zigzag, a giggle is two notes. The
 * speaker stays as the constant that says "this is a NOISE" and the thing
 * coming out of it is the animal's own.
 *
 * KEYED ON THE `sound` CUE ID, so it is a data drop like everything else here:
 * a new species that reuses a cue gets its picture for free, and one with a new
 * cue that nobody has drawn yet falls back to plain sound waves rather than to
 * nothing.
 */
const SOUND_PIC: Readonly<Record<string, string>> = {
  // a coiled spring — BOING
  toy_boing: `<path d="M62 26 C86 26 86 40 62 40 C86 40 86 54 62 54 C86 54 86 68 62 68 C86 68 86 80 66 80"
                ${OUT} fill="none" stroke="${css('uiPrimary')}"/>`,
  // a falling drop and a ripple — PLOP
  water_plop: `<path d="M72 22 C82 38 88 46 88 54 C88 63 81 70 72 70 C63 70 56 63 56 54 C56 46 62 38 72 22 Z"
                 fill="${css('water')}" ${OUT}/>
               <path d="M56 82 C62 76 82 76 88 82" ${OUT} fill="none" stroke="${css('waterDeep')}"/>`,
  // two rows of teeth closing — MUNCH
  munch: `<path d="M56 30 L92 30 L92 44 L86 38 L80 44 L74 38 L68 44 L62 38 L56 44 Z"
            fill="${css('flowerWhite')}" ${OUT}/>
          <path d="M56 76 L92 76 L92 62 L86 68 L80 62 L74 68 L68 62 L62 68 L56 62 Z"
            fill="${css('flowerWhite')}" ${OUT}/>`,
  // a tiny quick zigzag — NIBBLE
  nibble: `<path d="M58 62 L66 38 L74 62 L82 38 L90 62" ${OUT} fill="none" stroke="${css('uiAccent')}"/>`,
  // two notes — GIGGLE
  giggle: `<circle cx="64" cy="70" r="10" fill="${css('flowerPink')}" ${OUT}/>
           <path d="M74 70 L74 26 L92 20 L92 60" ${OUT} fill="none"/>
           <circle cx="86" cy="62" r="9" fill="${css('flowerPurple')}" ${OUT}/>`,
}

const SOUND_WAVES = `
  <path d="M62 36 C70 44 70 56 62 64" ${OUT} fill="none"/>
  <path d="M76 26 C90 42 90 58 76 74" ${OUT} fill="none"/>`

/** A speaker and this animal's own noise — "tap to hear what it says". */
export function soundEl(cue: string, sizePx: number): HTMLSpanElement {
  return svgEl(
    `<path d="M8 40 L22 40 L44 22 L44 78 L22 60 L8 60 Z" fill="${css('uiAccent')}" ${OUT}/>
     ${SOUND_PIC[cue] ?? SOUND_WAVES}`,
    sizePx,
    'mt-ico mt-zoo-snd',
  )
}

/** Test surface: which noises have a picture of their own. */
export function soundPicNames(): string[] {
  return Object.keys(SOUND_PIC).sort()
}
